// =============================================================================
// JEDYNE miejsce wywołań do organu Receptora z powłoki.
//
// ODCZYT IDZIE WPROST DO BAZY, ZAPIS PRZEZ USŁUGĘ — i to nie jest niekonsekwencja,
// tylko odwzorowanie tego, co naprawdę mówi RLS (migracje 729 i 731):
//
//   * `receptor_agents_tenant_read` — SELECT dla członków najemcy. Lista działa
//     więc kluczem zalogowanego człowieka i DZIAŁA TAKŻE WTEDY, gdy receptor-core
//     nie jest jeszcze wdrożony. Administrator widzi stan integracji, zanim
//     cokolwiek da się nią zrobić.
//   * do ZAPISU nie wpuszcza NIKOGO poza `service_role`. Panel nie może założyć
//     ani odwołać agenta sam — robi to `receptor-core`, gdzie tożsamość sprawdza
//     `bramaUzytkownika.ts` (JWT Supabase + realne członkostwo).
//
// To różnica wobec `printCoreApi.ts`, gdzie odczyt też jedzie przez usługę:
// `print_agents` nie ma polityki odczytu dla najemcy. Nie przepisuję tamtego
// ograniczenia tutaj tylko dla symetrii — kosztowałoby pustą listę na ekranie
// przed wdrożeniem usługi.
//
// `VITE_RECEPTOR_CORE_URL` MOŻE BYĆ PUSTE (usługa nie stoi jeszcze na Railway).
// Wtedy akcje zapisu są wyłączone z jednym, wspólnym komunikatem, a odczyt
// działa normalnie. ŻADNEJ zgadywanej domeny: adres pochodzi wyłącznie z env.
// =============================================================================

import { STATUS_AKTYWNEJ_LOKALIZACJI } from '@insolitum/utils';

import { supabase } from '../../../lib/supabase';

/** Tooltip każdej wyłączonej akcji zapisu — jeden tekst, jedno źródło. */
export const KOMUNIKAT_BRAK_RECEPTOR_CORE = 'Organ Receptora nie jest jeszcze wdrożony';

/**
 * Adres czytany PRZY KAŻDYM wywołaniu (funkcja, nie stała modułu), żeby testy
 * mogły go podstawić przez `vi.stubEnv` bez ponownego importu modułu.
 */
export function adresReceptorCore(): string {
  const surowy = (import.meta.env.VITE_RECEPTOR_CORE_URL as string | undefined) ?? '';
  return surowy.trim().replace(/\/+$/, '');
}

export function receptorCoreDostepny(): boolean {
  return adresReceptorCore().length > 0;
}

/** Błąd toru Receptora: status HTTP + kod `{error}` + komunikat `{message}`. */
export class ReceptorCoreError extends Error {
  constructor(
    public readonly status: number,
    public readonly kod: string,
    komunikat: string
  ) {
    super(komunikat);
    this.name = 'ReceptorCoreError';
  }
}

/**
 * Wiersz kartoteki w kształcie, jakim oddaje go usługa (`agentPubliczny`)
 * i jakim czyta go RLS. `token_jti` NIE JEST tu wymieniony i nie ma go w
 * `KOLUMNY` — to uchwyt odwołania, a lista jest dla każdego członka najemcy.
 */
export interface AgentReceptora {
  id: string;
  organization_id: string;
  name: string;
  status: 'pending' | 'active' | 'revoked';
  device_id: string | null;
  app_version: string | null;
  platform: string | null;
  hostname: string | null;
  last_seen_at: string | null;
  registered_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
  /** Nastawy zlecenia (migracja 733) — wszystkie trzy albo żadna. */
  zlecenie_magazyn_zrodlowy: number | null;
  zlecenie_magazyn_docelowy: number | null;
  zlecenie_zmiany: number[] | null;
  /**
   * Meldunek konfiguracji (agent 0.3.2, D4): którą bazę i które API obsługuje
   * agent. OPCJONALNE w typie — wiersz z odczytu, który kolumn nie wybrał
   * (albo atrapa), ma je nieobecne, a nieobecne czyta się jak NULL.
   */
  receptor_api_adres?: string | null;
  receptor_baza_serwer?: string | null;
  receptor_baza_instancja?: string | null;
  receptor_baza_port?: number | null;
  receptor_baza_nazwa?: string | null;
  receptor_baza_konto?: 'odczyt' | 'piaskownica' | null;
  receptor_konfiguracja_potwierdzona?: boolean | null;
  receptor_meldunek_at?: string | null;
  receptor_meldunek_odrzucony?: string | null;
  receptor_nastawy_zdjete_at?: string | null;
  /** Od kiedy agent melduje obecny komplet (API + baza) — granica aktualności odczytu nastaw. */
  receptor_komplet_od?: string | null;
  /**
   * Wersja Receptora z pulsu (agent 0.5.2, migracja 760). NULL źródła = agent
   * nie podaje (wersja sprzed 0.5.2) — to NIE jest `nie_podaje`.
   */
  receptor_wersja?: string | null;
  receptor_wersja_kod?: number | null;
  receptor_wersja_zrodlo?: 'api' | 'schemat' | 'nie_podaje' | null;
  receptor_wersja_slad?: Record<string, unknown> | null;
  receptor_wersja_at?: string | null;
  /**
   * Zdolności agenta z pulsu (UD-4, migracja 785). NULL `zdolnosci_at` = agent
   * nie podaje; `receptor_zdolnosci` NULL przy podanej gałęzi = nie sprawdzono.
   */
  zdolnosci?: string[] | null;
  receptor_zdolnosci?: string[] | null;
  receptor_zdolnosci_powod?: string | null;
  receptor_zdolnosci_at?: string | null;
  zdolnosci_at?: string | null;
  /**
   * Okres pulsu zgłoszony przez agenta (agent 0.8.0, migracja 840), 5–300 s.
   * NULL = agent nie podaje (0.7.x) = 60 s. Próg „offline” liczy z niego
   * `stanAgentaReceptora` (plan 59, K7).
   */
  puls_okres_s?: number | null;
}

/**
 * Zamknięta lista kolumn — `select('*')` wyniósłby `token_jti` na ekran.
 * Kolumny `receptor_*` wymagają migracji `750_receptor_zmiany_puste_i_meldunek_agenta.sql`,
 * `receptor_wersja*` — `760_receptor_wersja_receptora.sql`, zdolności —
 * `785_receptor_zdolnosci_agenta.sql`, a `puls_okres_s` — `840_receptor_puls_okres.sql`.
 */
const KOLUMNY =
  'id, organization_id, name, status, device_id, app_version, platform, hostname, last_seen_at, registered_at, revoked_at, created_at, updated_at, zlecenie_magazyn_zrodlowy, zlecenie_magazyn_docelowy, zlecenie_zmiany, ' +
  'receptor_api_adres, receptor_baza_serwer, receptor_baza_instancja, receptor_baza_port, receptor_baza_nazwa, receptor_baza_konto, receptor_konfiguracja_potwierdzona, receptor_meldunek_at, receptor_meldunek_odrzucony, receptor_nastawy_zdjete_at, receptor_komplet_od, ' +
  'receptor_wersja, receptor_wersja_kod, receptor_wersja_zrodlo, receptor_wersja_slad, receptor_wersja_at, ' +
  'zdolnosci, receptor_zdolnosci, receptor_zdolnosci_powod, receptor_zdolnosci_at, zdolnosci_at, ' +
  'puls_okres_s';

/** Lista agentów organizacji — kluczem użytkownika, przez `_tenant_read`. */
export async function listaAgentow(organizationId: string): Promise<AgentReceptora[]> {
  const { data, error } = await supabase
    .from('receptor_agents')
    .select(KOLUMNY)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_kartoteki',
      `Nie udało się wczytać agentów: ${error.message}`
    );
  }
  return (data ?? []) as unknown as AgentReceptora[];
}

/**
 * Stan licencji Agenta Integracji Receptor 3.0 (per serwer) — werdykt
 * `receptor_licencja` w trybie organizacji plus pozycja każdego aktywnego
 * agenta (pracują najstarsi, D5). Odczyt WPROST z bazy (RPC dla członka
 * organizacji), więc działa także, gdy receptor-core nie odpowiada.
 */
export interface StanLicencjiReceptora {
  ok: boolean;
  kod: (typeof KODY_LICENCJI_AGENTA)[number] | null;
  komunikat: string | null;
  /** `null` przy „bez limitu" albo braku licencji — rozstrzyga `bez_limitu`. */
  serwery_limit: number | null;
  bez_limitu: boolean;
  serwery_zajete: number;
  /** `monthly` | `yearly` | `on_premise` | `nadanie` | null. */
  okres: string | null;
  /** Ostatni WAŻNY dzień, YYYY-MM-DD (Europe/Warsaw). */
  wazna_do: string | null;
  laska: boolean;
  laska_do: string | null;
  agenci: Array<{ id: string; pozycja: number; w_licencji: boolean }>;
}

export async function stanLicencjiReceptora(
  organizationId: string
): Promise<StanLicencjiReceptora> {
  const { data, error } = await supabase.rpc('receptor_licencja_stan', {
    p_organization_id: organizationId,
  });
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_licencji',
      `Nie udało się wczytać stanu licencji: ${error.message}`
    );
  }
  return data as StanLicencjiReceptora;
}

async function naglowki(organizationId: string): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new ReceptorCoreError(401, 'brak_sesji', 'Brak aktywnej sesji użytkownika');
  }
  // `x-organization-id` to PROŚBA, nie źródło prawdy — brama usługi sprawdza
  // członkostwo po swojej stronie i może ją odrzucić.
  return { Authorization: `Bearer ${token}`, 'x-organization-id': organizationId };
}

async function wolaj<T>(
  organizationId: string,
  sciezka: string,
  opcje: { method?: 'GET' | 'POST' | 'PUT'; body?: unknown } = {}
): Promise<T> {
  const baza = adresReceptorCore();
  if (!baza) {
    // Zapis bez wdrożonej usługi to błąd programisty UI (przyciski mają być
    // wyłączone) — ale bronimy się i tutaj, tym samym komunikatem.
    throw new ReceptorCoreError(0, 'receptor_core_niedostepny', KOMUNIKAT_BRAK_RECEPTOR_CORE);
  }
  const headers = await naglowki(organizationId);
  if (opcje.body !== undefined) headers['Content-Type'] = 'application/json';

  let odpowiedz: Response;
  try {
    odpowiedz = await fetch(`${baza}${sciezka}`, {
      method: opcje.method ?? 'GET',
      headers,
      body: opcje.body !== undefined ? JSON.stringify(opcje.body) : undefined,
    });
  } catch {
    throw new ReceptorCoreError(0, 'siec', 'Nie udało się połączyć z organem Receptora');
  }

  if (!odpowiedz.ok) {
    let kod = 'blad';
    let komunikat = `Błąd organu Receptora (HTTP ${odpowiedz.status})`;
    try {
      const cialo = (await odpowiedz.json()) as { error?: string; message?: string };
      if (cialo.error) kod = cialo.error;
      if (cialo.message) komunikat = cialo.message;
    } catch {
      // Ciało nie było JSON-em — zostaje komunikat ogólny.
    }
    throw new ReceptorCoreError(odpowiedz.status, kod, komunikat);
  }
  return (await odpowiedz.json()) as T;
}

/** Krok 1 — administrator dodaje wpis agenta. Sam wiersz nie daje dostępu. */
export async function zarejestrujAgenta(
  organizationId: string,
  name: string
): Promise<AgentReceptora> {
  const dane = await wolaj<{ agent: AgentReceptora }>(organizationId, '/api/receptor/agents', {
    method: 'POST',
    body: { name },
  });
  return dane.agent;
}

/**
 * Kody odmowy LICENCJI agenta (per serwer) — co do znaku te same, co
 * `RAISE EXCEPTION` w `receptor_licencja` i `KODY_LICENCJI` w receptor-core
 * (bramka `kodyLicencjiReceptora.test.ts` zestawia wszystkie kopie).
 */
export const KODY_LICENCJI_AGENTA = [
  'license_missing',
  'license_expired',
  'server_limit_reached',
] as const;

/**
 * Powody odmowy z `receptor_agent_nowy_kod_parowania` — przepisane z migracji
 * 729 i uzupełnione o kody licencji (migracja 754: kod nie wychodzi, gdy
 * licencja nie obejmuje kolejnego serwera — odmowa przy biurku, nie przy
 * serwerze).
 */
export const POWODY_KODU = [
  'agent_not_found',
  'agent_revoked',
  'forbidden',
  'code_collision',
  ...KODY_LICENCJI_AGENTA,
] as const;

export interface KodParowaniaAgenta {
  code: string;
  expires_at: string;
  agent_id: string;
  agent_name: string;
}

/**
 * Krok 2 — kod wystawia BAZA, nie usługa.
 *
 * Funkcja jest SECURITY DEFINER i sama sprawdza członkostwo oraz rolę po
 * `auth.uid()`, więc panel woła ją kluczem zalogowanego administratora —
 * dokładnie tak, jak przy tablecie i serwerze wydruku. Kod jawny istnieje
 * WYŁĄCZNIE w tej odpowiedzi; w bazie zostaje bcrypt.
 *
 * Działa NIEZALEŻNIE od tego, czy receptor-core stoi: wystawienie kodu nie
 * wymaga usługi. Dopiero agent wymienia go na token przez `/enroll`.
 */
export async function kodParowaniaAgenta(
  agentId: string,
  ttlMinut = 15
): Promise<KodParowaniaAgenta> {
  const { data, error } = await supabase.rpc('receptor_agent_nowy_kod_parowania', {
    p_agent_id: agentId,
    p_ttl_minutes: ttlMinut,
  });
  if (error) {
    // Funkcja sygnalizuje powody przez `RAISE EXCEPTION '<kod>'` i dokłada
    // czytelny HINT. PostgREST podaje surowy kod w `message`, a HINT w `hint`.
    const powod = POWODY_KODU.find((p) => error.message.includes(p)) ?? 'pairing_code_failed';
    const hint = (error as { hint?: string | null }).hint;
    const licencja = (KODY_LICENCJI_AGENTA as readonly string[]).includes(powod);
    throw new ReceptorCoreError(
      powod === 'forbidden' || licencja ? 403 : 400,
      powod,
      hint || 'Nie udało się wygenerować kodu parowania.'
    );
  }
  return data as KodParowaniaAgenta;
}

/** Krok 3 — kill switch. Token martwy natychmiast, bez czekania na `exp`. */
export async function odwolajAgenta(organizationId: string, id: string): Promise<void> {
  await wolaj(organizationId, `/api/receptor/agents/${id}/revoke`, { method: 'POST' });
}

// ── Kolejka zadań (migracja 732) ─────────────────────────────────────────────
//
// Ten sam podział, co wyżej: ODCZYT wprost z bazy (`receptor_agent_tasks_tenant_read`
// daje SELECT członkowi najemcy), ZLECENIE przez usługę (do zapisu RLS wpuszcza
// wyłącznie `service_role`, a usługa sprawdza rodzaj i przynależność agenta).
//
// Rodzaje, statusy i streszczenie wyniku mieszkają w `rodzajeZadanReceptora.ts`
// (bez zależności od Supabase), żeby bramka z korzenia repozytorium mogła je
// zestawić z listą usługi — patrz nagłówek tamtego pliku.

export {
  OPIS_STATUSU_PINU,
  OPIS_STATUSU_ZADANIA,
  RODZAJE_ZADAN_PANELU,
  RODZAJ_ODCZYTU_DOKUMENTOW,
  RODZAJ_ODCZYTU_NASTAW,
  RODZAJ_ODCZYTU_UZYTKOWNIKOW,
  RODZAJ_POBRANIA_PINU,
  RODZAJ_POTWIERDZENIA_DOKUMENTOW,
  STATUSY_PINU,
  WERSJA_PRACOWNIKOW_RECEPTORA,
  WERSJA_ZLECEN_BEZ_ZMIANY,
  ZRODLO_HARMONOGRAMU,
  etykietaPozycji,
  granicaOdczytuNastaw,
  odczytNastawAktualny,
  opisMeldunku,
  opisSynchronizacjiDokumentow,
  opisWersjiReceptora,
  wersjaAgentaCoNajmniej,
  slownikiZWyniku,
  statusPinuZWyniku,
  streszczenieWyniku,
  zadanieHarmonogramu,
  zadanieWToku,
  zrodloZadania,
  type OpisRodzajuZadania,
  type OpisStatusuZadania,
  type PozycjaSlownikaReceptora,
  type SlownikiNastaw,
  type StatusPinu,
  type StatusZadania,
  type ZadanieAgenta,
} from './rodzajeZadanReceptora';
import {
  KOLUMNY_KURSORA_OBIEGU,
  granicePytaniaOZaleglosc,
  type KursorObiegu,
  type ZakladObiegu,
  type ZakladOrganizacji,
} from './obiegDokumentow';
import {
  stanyPracownikowReceptora,
  type StanReceptoraPracownika,
  type WierszPowiazaniaReceptora,
} from './pracownicyReceptora';
import {
  RODZAJ_ODCZYTU_NASTAW,
  RODZAJ_ODCZYTU_UZYTKOWNIKOW,
  RODZAJ_POBRANIA_PINU,
  ZRODLO_HARMONOGRAMU,
  type ZadanieAgenta,
} from './rodzajeZadanReceptora';

/** Zamknięta lista kolumn — `zlecil` i `updated_at` nie są ekranowi potrzebne. */
const KOLUMNY_ZADANIA =
  'id, organization_id, agent_id, rodzaj, parametry, status, blad, proby, zrodlo_system, zrodlo_id, created_at, podjete_at, podjete_przez, zakonczone_at, wynik';

/** Ile ostatnich zadań pokazuje ekran — `wynik` bywa duży, więc lista jest krótka. */
export const LIMIT_LISTY_ZADAN_PANELU = 10;

/**
 * Filtr listy „Ostatnie zadania”: bez zadań harmonogramu obiegu dokumentów.
 *
 * `.or()` z `is.null`, NIE sam `.neq('zrodlo_system', 'harmonogram')`: w SQL
 * `NULL <> 'harmonogram'` nie jest prawdą, więc samo `neq` wyrzuciłoby także
 * wszystkie zadania z panelu (`zrodlo_system` NULL) — czyli cały ekran.
 * Harmonogram zakłada około 36 zadań na agenta na godzinę: bez filtra 10 pozycji
 * listy zalałoby się w kilkanaście minut, a każde zadanie w toku trzymałoby ekran
 * na szybkim odpytywaniu (`cosWToku` w `AgenciReceptora.tsx`) bez końca.
 */
export const FILTR_ZADAN_BEZ_HARMONOGRAMU = `zrodlo_system.is.null,zrodlo_system.neq.${ZRODLO_HARMONOGRAMU}`;

/** Ostatnie zadania organizacji — kluczem użytkownika, przez `_tenant_read`. */
export async function listaZadan(
  organizationId: string,
  limit = LIMIT_LISTY_ZADAN_PANELU
): Promise<ZadanieAgenta[]> {
  const { data, error } = await supabase
    .from('receptor_agent_tasks')
    .select(KOLUMNY_ZADANIA)
    .eq('organization_id', organizationId)
    .or(FILTR_ZADAN_BEZ_HARMONOGRAMU)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    throw new ReceptorCoreError(0, 'odczyt_zadan', `Nie udało się wczytać zadań: ${error.message}`);
  }
  return (data ?? []) as unknown as ZadanieAgenta[];
}

/** Zlecenie zadania — przez usługę, bo tylko ona ma prawo zapisu do kolejki. */
export async function zlecZadanie(
  organizationId: string,
  wejscie: { rodzaj: string; agent_id?: string | null; parametry?: Record<string, unknown> }
): Promise<ZadanieAgenta> {
  const dane = await wolaj<{ task: ZadanieAgenta }>(organizationId, '/api/receptor/tasks', {
    method: 'POST',
    body: {
      rodzaj: wejscie.rodzaj,
      agent_id: wejscie.agent_id ?? null,
      parametry: wejscie.parametry ?? {},
    },
  });
  return dane.task;
}

/**
 * Ostatni odczyt magazynów i zmian TEGO agenta (zadanie `zrobione`) — źródło
 * list wyboru w nastawach. Osobne zapytanie, a nie przeszukanie listy „Ostatnie
 * zadania": ta ma dziesięć pozycji, a odczyt nastaw robi się raz na wdrożenie.
 */
export async function ostatniOdczytNastaw(
  organizationId: string,
  agentId: string
): Promise<ZadanieAgenta | null> {
  const { data, error } = await supabase
    .from('receptor_agent_tasks')
    .select(KOLUMNY_ZADANIA)
    .eq('organization_id', organizationId)
    .eq('agent_id', agentId)
    .eq('rodzaj', RODZAJ_ODCZYTU_NASTAW)
    .eq('status', 'zrobione')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zadan',
      `Nie udało się wczytać magazynów i zmian: ${error.message}`
    );
  }
  const wiersze = (data ?? []) as unknown as ZadanieAgenta[];
  return wiersze[0] ?? null;
}

export interface NastawyZlecenia {
  magazyn_zrodlowy: number;
  magazyn_docelowy: number;
  zmiany: number[];
}

/**
 * Zapis nastaw zlecenia przy agencie — przez usługę (RLS wpuszcza do zapisu
 * wyłącznie `service_role`). `null` czyści nastawy.
 */
export async function zapiszNastawy(
  organizationId: string,
  agentId: string,
  nastawy: NastawyZlecenia | null
): Promise<AgentReceptora> {
  const dane = await wolaj<{ agent: AgentReceptora }>(
    organizationId,
    `/api/receptor/agents/${agentId}/nastawy`,
    {
      method: 'PUT',
      body: nastawy ?? { magazyn_zrodlowy: null, magazyn_docelowy: null, zmiany: null },
    }
  );
  return dane.agent;
}

/** Jedno zadanie po id — okno nastaw czeka na wynik odczytu, który właśnie zleciło. */
export async function zadanieWgId(
  organizationId: string,
  id: string
): Promise<ZadanieAgenta | null> {
  const { data, error } = await supabase
    .from('receptor_agent_tasks')
    .select(KOLUMNY_ZADANIA)
    .eq('organization_id', organizationId)
    .eq('id', id)
    .maybeSingle();
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zadan',
      `Nie udało się wczytać zadania: ${error.message}`
    );
  }
  return (data as unknown as ZadanieAgenta | null) ?? null;
}

// ── Pracownicy ↔ osoby z Receptora (migracja 758, agent 0.5.0) ──────────────
//
// Ten sam podział, co wyżej. ODCZYT wprost z bazy: lista osób to wynik zadania
// (`receptor_agent_tasks`, RLS: owner/admin/manager), powiązania to wiersze
// `shop_floor_worker_external_ids` z `system = 'receptor'` (RLS: członkowie).
// ZAPIS przez usługę: powiązanie i zlecenie pobrania PIN-u robi receptor-core,
// bo PIN jedzie od agenta wprost do `receptor_pin_przypisz` i nie ma prawa
// przejść przez panel ani przez `wynik` zadania.

/** Zlecenie odczytu osób z Receptora — wyłącznie przez KONKRETNEGO agenta (≥ 0.5.0). */
export async function odczytajOsobyReceptora(
  organizationId: string,
  agentId: string
): Promise<ZadanieAgenta> {
  return zlecZadanie(organizationId, {
    rodzaj: RODZAJ_ODCZYTU_UZYTKOWNIKOW,
    agent_id: agentId,
    parametry: { tylko_aktywni: true },
  });
}

/**
 * Ostatni UDANY odczyt osób przez TEGO agenta — IdUser ma sens tylko w bazie,
 * którą czyta ten agent, więc listy dwóch agentów się nie miesza. Wzorzec
 * `ostatniOdczytNastaw`.
 */
export async function ostatniOdczytOsob(
  organizationId: string,
  agentId: string
): Promise<ZadanieAgenta | null> {
  const { data, error } = await supabase
    .from('receptor_agent_tasks')
    .select(KOLUMNY_ZADANIA)
    .eq('organization_id', organizationId)
    .eq('agent_id', agentId)
    .eq('rodzaj', RODZAJ_ODCZYTU_UZYTKOWNIKOW)
    .eq('status', 'zrobione')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zadan',
      `Nie udało się wczytać osób z Receptora: ${error.message}`
    );
  }
  const wiersze = (data ?? []) as unknown as ZadanieAgenta[];
  return wiersze[0] ?? null;
}

export interface WejsciePowiazania {
  agent_id: string;
  id_user: number;
  /** Domyślnie `true` — `false`, gdy PIN zostaje wygenerowany w Universe. */
  pobierz_pin: boolean;
}

export interface WynikPowiazania {
  powiazanie: { worker_id: string; agent_id: string; id_user: number };
  /** Zadanie pobrania PIN-u albo `null`, gdy `pobierz_pin === false`. */
  task: ZadanieAgenta | null;
}

/**
 * Powiązanie pracownika z osobą z Receptora (+ opcjonalnie zlecenie pobrania
 * PIN-u). Odmowy: `worker_not_found`, `agent_not_active`, `agent_za_stary`,
 * `receptor_user_taken`, kody licencji.
 */
export async function powiazZReceptorem(
  organizationId: string,
  workerId: string,
  wejscie: WejsciePowiazania
): Promise<WynikPowiazania> {
  return wolaj<WynikPowiazania>(
    organizationId,
    `/api/receptor/pracownicy/${encodeURIComponent(workerId)}/receptor`,
    {
      method: 'POST',
      body: {
        agent_id: wejscie.agent_id,
        id_user: wejscie.id_user,
        pobierz_pin: wejscie.pobierz_pin,
      },
    }
  );
}

/**
 * Ponowne pobranie PIN-u dla już powiązanego pracownika. Gdy zadanie tego
 * pracownika jeszcze czeka, usługa oddaje JE zamiast zakładać drugie.
 * Odmowy: `receptor_link_not_found`, `agent_not_active`, `agent_za_stary`.
 */
export async function pobierzPinZReceptora(
  organizationId: string,
  workerId: string
): Promise<ZadanieAgenta> {
  const dane = await wolaj<{ task: ZadanieAgenta }>(
    organizationId,
    `/api/receptor/pracownicy/${encodeURIComponent(workerId)}/pin`,
    { method: 'POST' }
  );
  return dane.task;
}

/** Ile ostatnich zadań pin czytamy naraz — po jednym na pracownika z zapasem. */
export const LIMIT_ZADAN_PINU = 500;

/**
 * Powiązania pracowników z Receptorem + najnowsze zadanie pin każdego z nich.
 * Kolumny zadania są te same, co listy zadań: `wynik` zadania pin niesie
 * wyłącznie `{status, id_user, worker_id}` (PIN nie trafia do bazy jawnie),
 * a `parametry` — `{id_user, worker_id}`.
 */
export async function stanReceptoraPracownikow(
  organizationId: string
): Promise<Map<string, StanReceptoraPracownika>> {
  const [powiazania, zadania] = await Promise.all([
    supabase
      .from('shop_floor_worker_external_ids')
      .select('worker_id, company, external_id, acronym, last_status, updated_at')
      .eq('organization_id', organizationId)
      .eq('system', 'receptor'),
    supabase
      .from('receptor_agent_tasks')
      .select(KOLUMNY_ZADANIA)
      .eq('organization_id', organizationId)
      .eq('rodzaj', RODZAJ_POBRANIA_PINU)
      .order('created_at', { ascending: false })
      .limit(LIMIT_ZADAN_PINU),
  ]);
  if (powiazania.error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_powiazan',
      `Nie udało się wczytać powiązań z Receptorem: ${powiazania.error.message}`
    );
  }
  if (zadania.error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zadan',
      `Nie udało się wczytać stanu PIN-ów: ${zadania.error.message}`
    );
  }
  return stanyPracownikowReceptora(
    (powiazania.data ?? []) as unknown as WierszPowiazaniaReceptora[],
    (zadania.data ?? []) as unknown as ZadanieAgenta[]
  );
}

// ── Obieg dokumentów (UD-11a, migracja 786, plan 53 §13.6.4 p. 9) ───────────
//
// Ten sam podział, co wyżej: ODCZYT wprost z bazy, ZAPIS przez usługę.
//
//   * `receptor_kursory` i `commerce_documents` czyta `authenticated` WYŁĄCZNIE
//     przez grant na NAZWANE kolumny i pod polityką `is_super_admin() OR
//     is_org_writer(...)` (owner, admin, manager). Dlatego lista kolumn jest tu
//     zamknięta (`KOLUMNY_KURSORA_OBIEGU`), a `select('*')` jest błędem 42501.
//   * ZAPIS (włącz, wstrzymaj, wznów, przepnij) robi receptor-core: klient nie ma
//     w tych tabelach żadnego INSERT/UPDATE, a rolę sprawdza brama usługi
//     (`ROLE_OBIEGU`, przepięcie — wyłącznie super-admin).
//
// Odmowa usługi to `{ error: <kod>, message: <zdanie po polsku> }` — `wolaj`
// oddaje je jako `ReceptorCoreError`, a ekran pokazuje `message` dosłownie
// (mówi, CO ZROBIĆ: „prowadzi go inny agent”, „zakład nie ma strefy czasowej”).

/** Kursory obiegu organizacji, najstarszy pierwszy (tak wybiera funkcja włączenia). */
export async function listaKursorowObiegu(organizationId: string): Promise<KursorObiegu[]> {
  const { data, error } = await supabase
    .from('receptor_kursory')
    .select(KOLUMNY_KURSORA_OBIEGU.join(', '))
    .eq('organization_id', organizationId)
    .eq('strumien', 'dokumenty')
    .order('aktywowano_at', { ascending: true });
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_obiegu',
      `Nie udało się wczytać stanu obiegu dokumentów: ${error.message}`
    );
  }
  return (data ?? []) as unknown as KursorObiegu[];
}

/**
 * Zakłady do NOWEGO włączenia obiegu: tylko AKTYWNE lokalizacje organizacji
 * (`status = 'active'`, P2 — ta sama reguła co odmowa `zaklad_nieaktywny`
 * w `receptor_obieg_aktywuj`). Nazwy zakładów już związanych z kursorami
 * i dokumentami czyta {@link zakladyOrganizacji}, bez filtra statusu. Czyta je
 * każdy członek organizacji (polityka „Users can view org locations”), więc lista
 * działa także dla kierownika, który obieg tylko ogląda.
 */
export async function listaZakladow(organizationId: string): Promise<ZakladObiegu[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('id, name, timezone')
    .eq('organization_id', organizationId)
    .eq('status', STATUS_AKTYWNEJ_LOKALIZACJI)
    .order('name', { ascending: true });
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zakladow',
      `Nie udało się wczytać zakładów organizacji: ${error.message}`
    );
  }
  return (data ?? []) as unknown as ZakladObiegu[];
}

/**
 * Lokalizacje organizacji ZE STATUSEM i BEZ filtra statusu — nazwy zakładów już
 * związanych: z kursorami (karta agenta, `ids` = `zaklad_id` kursorów) i z
 * dokumentami (lista „Obieg dokumentów”, bez `ids`). Zakład usunięty na ekranie
 * Lokalizacje zostaje `inactive` (most 791) i ma zachować nazwę, a nie dostać
 * „zakład spoza listy”. Organizacja jest w filtrze JAWNIE: super-admin widzi
 * wszystkie (polityka „Users can view org locations”). Puste `ids` → `[]` bez
 * zapytania (organizacja bez kursora nie płaci za odczyt).
 */
export async function zakladyOrganizacji(
  organizationId: string,
  { ids }: { ids?: readonly string[] } = {}
): Promise<ZakladOrganizacji[]> {
  const unikalne = ids === undefined ? undefined : [...new Set(ids)];
  if (unikalne !== undefined && unikalne.length === 0) return [];
  let zapytanie = supabase
    .from('locations')
    .select('id, name, timezone, status')
    .eq('organization_id', organizationId);
  if (unikalne !== undefined) zapytanie = zapytanie.in('id', unikalne);
  const { data, error } = await zapytanie.order('name', { ascending: true });
  if (error) {
    throw new ReceptorCoreError(
      0,
      'odczyt_zakladow_organizacji',
      `Nie udało się wczytać nazw zakładów: ${error.message}`
    );
  }
  return (data ?? []) as unknown as ZakladOrganizacji[];
}

/**
 * Ile potwierdzonych dokumentów kontrola sprawdziła dawniej niż 12 h temu —
 * „kontrola nie nadąża” (§13.6.4 p. 7). Dokumenty potwierdzone w ostatnich
 * 35 dniach, wciąż `zarejestrowany` u źródła. Zliczenie `head` — bez wierszy,
 * bez treści. Organizacja jest w filtrze JAWNIE: super-admin widzi wszystkie
 * organizacje, więc sama polityka nie zawęża.
 *
 * Brak liczby w odpowiedzi to błąd, nie zero: „0 zaległych” oznacza dla
 * człowieka, że kontrola działa.
 */
export async function liczbaZaleglychKontroli(
  organizationId: string,
  teraz: number = Date.now()
): Promise<number> {
  const { potwierdzoneOd, sprawdzoneDo } = granicePytaniaOZaleglosc(teraz);
  const { count, error } = await supabase
    .from('commerce_documents')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('stan_zrodla', 'zarejestrowany')
    .eq('wystawca_potwierdzenie', 'potwierdzony')
    .gte('wystawca_potwierdzenie_at', potwierdzoneOd)
    .lt('wystawca_sprawdzono_at', sprawdzoneDo);
  if (error || typeof count !== 'number') {
    throw new ReceptorCoreError(
      0,
      'odczyt_dokumentow',
      `Nie udało się sprawdzić zaległości kontroli dokumentów${error ? `: ${error.message}` : ''}`
    );
  }
  return count;
}

/** Nastawy z formularza włączenia — `od` w UTC z `Z`. */
export interface WejscieWlaczeniaObiegu {
  zaklad_id: string;
  od: string;
  karencja_min: number;
  limit_strony: number;
  limit_czasu_s: number;
}

export interface WynikWlaczeniaObiegu {
  ok: boolean;
  kursor_id: string;
  nowy: boolean;
  przeniesiony: boolean;
  stan: string;
  zaklad_id: string;
  od: string;
  po_seq: number;
  instalacja: string | null;
  karencja_min: number;
  limit_strony: number;
  limit_czasu_s: number;
}

export interface WynikZmianyStanuObiegu {
  ok: boolean;
  /** `false` = obieg już był w tym stanie (wywołanie idempotentne). */
  zmiana: boolean;
  stan: string;
  /** Stan sprzed zmiany. */
  z?: string;
}

export interface WynikPrzepieciaObiegu {
  ok: boolean;
  stan: string;
  po_seq: number;
  od: string;
  /** Stan zatrzymania, z którego przepięto. */
  stan_z?: string;
}

const sciezkaObiegu = (agentId: string) =>
  `/api/receptor/agents/${encodeURIComponent(agentId)}/obieg-dokumentow`;

/**
 * Włączenie obiegu przy agencie (owner, admin, super-admin). Odmowy:
 * 400 `nastawy_poza_zakresem` | `od_poza_zakresem`; 404 `agent_not_found` |
 * `zaklad_obcy`; 409 `agent_not_active` | `agent_bez_zdolnosci` |
 * `serwer_bez_trasy` | `straznik_odmawia` | `jeden_serwer_w_obiegu` |
 * `zaklad_bez_strefy` | `zaklad_nieaktywny` | `zaklad_zablokowany` | `agent_niedostepny`;
 * 403 licencja.
 */
export async function wlaczObieg(
  organizationId: string,
  agentId: string,
  wejscie: WejscieWlaczeniaObiegu
): Promise<WynikWlaczeniaObiegu> {
  const dane = await wolaj<{ obieg: WynikWlaczeniaObiegu }>(
    organizationId,
    sciezkaObiegu(agentId),
    {
      method: 'POST',
      body: {
        zaklad_id: wejscie.zaklad_id,
        od: wejscie.od,
        karencja_min: wejscie.karencja_min,
        limit_strony: wejscie.limit_strony,
        limit_czasu_s: wejscie.limit_czasu_s,
      },
    }
  );
  return dane.obieg;
}

/** Wstrzymanie aktywnego obiegu — działa w najbliższym pulsie (≤ 60 s). Powód zostaje w śladzie. */
export async function wstrzymajObieg(
  organizationId: string,
  agentId: string,
  wejscie: { powod: string; zaklad_id?: string }
): Promise<WynikZmianyStanuObiegu> {
  const dane = await wolaj<{ obieg: WynikZmianyStanuObiegu }>(
    organizationId,
    `${sciezkaObiegu(agentId)}/wstrzymaj`,
    { method: 'POST', body: { powod: wejscie.powod, zaklad_id: wejscie.zaklad_id } }
  );
  return dane.obieg;
}

/**
 * Wznowienie obiegu zatrzymanego stanem, który człowiek może naprawić
 * (`wstrzymany`, `kontrakt_nieznany`, `dokument_niepoprawny`, `dokument_za_duzy`).
 * Pozostałe stany: 409 `stan_automatyczny` albo `wymaga_przepiecia`.
 */
export async function wznowObieg(
  organizationId: string,
  agentId: string,
  wejscie: { powod: string; zaklad_id?: string }
): Promise<WynikZmianyStanuObiegu> {
  const dane = await wolaj<{ obieg: WynikZmianyStanuObiegu }>(
    organizationId,
    `${sciezkaObiegu(agentId)}/wznow`,
    { method: 'POST', body: { powod: wejscie.powod, zaklad_id: wejscie.zaklad_id } }
  );
  return dane.obieg;
}

/**
 * Przepięcie na nową instalację: `po_seq = 0`, nowe `od`, stary stan i powód
 * w śladzie. WYŁĄCZNIE super-admin — każdy inny dostaje 403 z bramy usługi.
 */
export async function przepnijObieg(
  organizationId: string,
  agentId: string,
  wejscie: { od: string; powod: string; zaklad_id?: string }
): Promise<WynikPrzepieciaObiegu> {
  const dane = await wolaj<{ obieg: WynikPrzepieciaObiegu }>(
    organizationId,
    `${sciezkaObiegu(agentId)}/przepnij`,
    {
      method: 'POST',
      body: { od: wejscie.od, powod: wejscie.powod, zaklad_id: wejscie.zaklad_id },
    }
  );
  return dane.obieg;
}

// ── Powiązania kart (UD-22b) ────────────────────────────────────────────────
//
// Odczyt i decyzje idą WYŁĄCZNIE przez usługę: `kartoteka_zrodla` i `kartoteka_propozycje`
// nie mają polityki odczytu dla `authenticated` (812 — sam `SELECT` dla `service_role`),
// a decyzje są funkcjami bazy wołanymi kluczem serwisowym z aktorem z JWT. Kształt
// odpowiedzi to kopia typów `services/receptor-core/src/powiazaniaKart.ts`.

/** Wynik ostatniej obserwacji spisu (832) — nazwane pola albo powód pominięcia. */
export type ObserwacjaSpisuKartoteki =
  | {
      ok: boolean | null;
      wstrzymana: boolean | null;
      mapa: number | null;
      brak: number | null;
      nieobecne: number | null;
      nagrobki: number | null;
    }
  | { pominieta: string };

export interface ZrodloKartoteki {
  stan: string;
  stan_powod: string | null;
  stan_at: string | null;
  agent_id: string | null;
  automat: boolean;
  aktywowano_at: string | null;
  vat_potwierdzono_at: string | null;
  przenosiny_zatwierdzono_at: string | null;
  ostatni_spis_at: string | null;
  ostatni_pelny_spis_at: string | null;
  kart: number | null;
  porazki_z_rzedu: number;
  ostatnia_porazka_kod: string | null;
  obserwacja_at: string | null;
  obserwacja_wynik: ObserwacjaSpisuKartoteki | null;
  bezpiecznik_zwolniony: boolean;
}

export type AkcjaPropozycji = 'przyjmij' | 'odrzuc' | 'ponow';

export interface PropozycjaKartoteki {
  id: string;
  klasa: string;
  powody: string[];
  odcisk: string;
  id_stock: number | null;
  stan: string;
  karta: {
    nazwa: string | null;
    symbol: string | null;
    typ: string | null;
    jednostka: string | null;
    vat: string | null;
    ean: string | null;
    aktywna: boolean | null;
    w_sprzedazy: boolean | null;
  };
  encja: {
    typ: 'product' | 'pozycja';
    id: string;
    nazwa: string | null;
    kod: string | null;
  } | null;
  created_at: string;
  updated_at: string;
  akcje: AkcjaPropozycji[];
}

export interface StanKartoteki {
  zrodlo: ZrodloKartoteki | null;
  otwarte: number;
  wg_klasy: Record<string, number>;
  propozycje: PropozycjaKartoteki[];
  odrzucone: PropozycjaKartoteki[];
  obcieto: boolean;
  nagrobki: { nieobecne: number; usuniete: number };
}

export interface WejscieWlaczeniaKartoteki {
  automat: boolean;
  vat_z_receptora: boolean;
  przenosiny_zgodne: boolean;
}

export interface WynikRozstrzygnieciaPropozycji {
  stan: 'przyjeta' | 'odrzucona' | 'nieaktualna';
  wynik?: Record<string, number>;
  nastepna_propozycja?: string | null;
}

/** Stan powiązań kart organizacji (owner, admin, super-admin — pozostali dostają 403). */
export async function stanKartoteki(organizationId: string): Promise<StanKartoteki> {
  const dane = await wolaj<{ kartoteka: StanKartoteki }>(organizationId, '/api/receptor/kartoteka');
  return dane.kartoteka;
}

/**
 * Włączenie albo zmiana ustawień przyjmowania kart przy agencie PROWADZĄCYM kartotekę
 * (`zrodlo.agent_id`). Odmowy: 400 `vat_niepotwierdzony` (automat bez potwierdzenia VAT),
 * 404 `agent_not_found`, 409 `agent_nie_prowadzi_kartoteki` | `zrodlo_nieznane` |
 * `zrodlo_zawieszone`.
 */
export async function wlaczKartoteke(
  organizationId: string,
  agentId: string,
  wejscie: WejscieWlaczeniaKartoteki
): Promise<{ stan: string; odswiezone: number }> {
  const dane = await wolaj<{ kartoteka: { stan: string; odswiezone: number } }>(
    organizationId,
    `/api/receptor/agents/${encodeURIComponent(agentId)}/kartoteka`,
    {
      method: 'POST',
      body: {
        automat: wejscie.automat,
        vat_z_receptora: wejscie.vat_z_receptora,
        przenosiny_zgodne: wejscie.przenosiny_zgodne,
      },
    }
  );
  return dane.kartoteka;
}

/** Wyłączenie przyjmowania kart — z powodem (1–500 znaków). */
export async function wylaczKartoteke(
  organizationId: string,
  agentId: string,
  powod: string
): Promise<{ stan: string }> {
  const dane = await wolaj<{ kartoteka: { stan: string } }>(
    organizationId,
    `/api/receptor/agents/${encodeURIComponent(agentId)}/kartoteka/wylacz`,
    { method: 'POST', body: { powod } }
  );
  return dane.kartoteka;
}

/**
 * Rozstrzygnięcie propozycji z odciskiem karty, którą człowiek widział. 409
 * `propozycja_zmieniona` = karta zmieniła się w Receptorze — panel pobiera listę od nowa.
 * Powód jedzie tylko wtedy, gdy jest (pusty → brak klucza).
 */
export async function rozstrzygnijPropozycje(
  organizationId: string,
  propozycjaId: string,
  wejscie: { decyzja: AkcjaPropozycji; odcisk: string; powod?: string }
): Promise<WynikRozstrzygnieciaPropozycji> {
  const powod = wejscie.powod?.trim();
  const dane = await wolaj<{ rozstrzygniecie: WynikRozstrzygnieciaPropozycji }>(
    organizationId,
    `/api/receptor/kartoteka/propozycje/${encodeURIComponent(propozycjaId)}`,
    {
      method: 'POST',
      body: {
        decyzja: wejscie.decyzja,
        odcisk: wejscie.odcisk,
        ...(powod ? { powod } : {}),
      },
    }
  );
  return dane.rozstrzygniecie;
}

/** Jednorazowe zwolnienie bezpiecznika nagrobka (832) — po wstrzymanym spisie. */
export async function zwolnijBezpiecznik(organizationId: string): Promise<{ zwolniony: true }> {
  const dane = await wolaj<{ bezpiecznik: { zwolniony: true } }>(
    organizationId,
    '/api/receptor/kartoteka/bezpiecznik',
    { method: 'POST', body: {} }
  );
  return dane.bezpiecznik;
}
